/**
 * generateRegistryKey - утиліта для генерації ключів реєстру контекстів CRUD ресурсів
 * 
 * 📝 КОНВЕНЦІЯ ІМЕНУВАННЯ КЛЮЧІВ РЕЄСТРУ
 * 
 * Формат: <ResourceName>.<Purpose>.<UniqueId?>
 * 
 * СПИСКИ:
 * - "Setting.User.mainList" - основний список на сторінці
 * - "Setting.User.adminsList" - список адмінів (вкладений)
 * - "Vehicle.Truck.documentsList.{truckUuid}" - список документів для конкретної вантажівки
 * 
 * КАРТКИ:
 * - "Setting.User.card.view.123" - картка перегляду (генерується автоматично)
 * - "Setting.User.card.edit.123" - картка редагування
 * - "Setting.User.card.create" - картка створення
 * 
 * URL-BASED (автоматичні):
 * - "Setting.User.urlList" - список відкритий через URL
 * - "Setting.User.urlCard.view.123" - картка з URL
 * 
 * @example
 * ```typescript
 * // Для основного списку
 * const key = generateRegistryKey.list('Setting.User')
 * // => "Setting.User.mainList"
 * 
 * // Для вкладеного списку
 * const key = generateRegistryKey.list('Setting.User', 'adminsList')
 * // => "Setting.User.adminsList"
 * 
 * // Для списку з прив'язкою до батьківського ресурсу
 * const key = generateRegistryKey.nestedList('Vehicle.TruckDocuments', 'Vehicle.Truck', truckUuid)
 * // => "Vehicle.TruckDocuments.nested.Vehicle.Truck.{uuid}"
 * 
 * // Для картки
 * const key = generateRegistryKey.card('Setting.User', 'edit', 123)
 * // => "Setting.User.card.edit.123"
 * ```
 */

import type { TResourceDetailMode } from '../Tasks/Resources/OpenResourceCardTask'

export const generateRegistryKey = {
    /**
     * Генерує ключ для списку ресурсу
     * 
     * @param resourceName - назва ресурсу (наприклад, "Setting.User")
     * @param purpose - призначення списку (за замовчуванням "mainList")
     * @returns ключ реєстру
     */
    list: (resourceName: string, purpose: string = 'mainList'): string => {
        if (!resourceName) {
            throw new Error('[generateRegistryKey.list] resourceName є обов\'язковим')
        }
        return `${resourceName}.${purpose}`
    },

    /**
     * Генерує ключ для картки ресурсу
     * 
     * @param resourceName - назва ресурсу
     * @param mode - режим картки ('view' | 'edit' | 'create')
     * @param itemId - ID елемента (опціонально для 'create')
     * @returns ключ реєстру
     */
    card: (
        resourceName: string, 
        mode: TResourceDetailMode | string, 
        itemId?: string | number
    ): string => {
        if (!resourceName) {
            throw new Error('[generateRegistryKey.card] resourceName є обов\'язковим')
        }
        if (!mode) {
            throw new Error('[generateRegistryKey.card] mode є обов\'язковим')
        }

        if (mode === 'create') {
            // Для create режиму додаємо timestamp, щоб кожне відкриття
            // створювало новий контекст і форма не зберігала попередні дані
            return `${resourceName}.card.${mode}.${Date.now()}`
        }
        
        if (!itemId) {
            return `${resourceName}.card.${mode}`
        }

        return `${resourceName}.card.${mode}.${itemId}`
    },

    /**
     * Генерує ключ для вкладеного списку (nested list)
     * Використовується коли список одного ресурсу відображається всередині іншого
     * 
     * @param resourceName - назва дочірнього ресурсу
     * @param parentResource - назва батьківського ресурсу
     * @param parentId - ID батьківського елемента
     * @returns ключ реєстру
     */
    nestedList: (
        resourceName: string, 
        parentResource: string, 
        parentId: string | number
    ): string => {
        if (!resourceName) {
            throw new Error('[generateRegistryKey.nestedList] resourceName є обов\'язковим')
        }
        if (!parentResource) {
            throw new Error('[generateRegistryKey.nestedList] parentResource є обов\'язковим')
        }
        if (parentId === undefined || parentId === null) {
            throw new Error('[generateRegistryKey.nestedList] parentId є обов\'язковим')
        }

        return `${resourceName}.nested.${parentResource}.${parentId}`
    },

    /**
     * Генерує ключ для списку відкритого через URL
     * Використовується в UniversalResourcePage
     * 
     * @param resourceName - назва ресурсу
     * @returns ключ реєстру
     */
    urlList: (resourceName: string): string => {
        if (!resourceName) {
            throw new Error('[generateRegistryKey.urlList] resourceName є обов\'язковим')
        }
        return `${resourceName}.urlList`
    },

    /**
     * Генерує ключ для картки відкритої через URL
     * Використовується в UniversalResourcePage для drawer/modal карток
     * 
     * @param resourceName - назва ресурсу
     * @param mode - режим картки
     * @param itemId - ID елемента
     * @returns ключ реєстру
     */
    urlCard: (
        resourceName: string, 
        mode: TResourceDetailMode | string, 
        itemId?: string | number
    ): string => {
        if (!resourceName) {
            throw new Error('[generateRegistryKey.urlCard] resourceName є обов\'язковим')
        }
        if (!mode) {
            throw new Error('[generateRegistryKey.urlCard] mode є обов\'язковим')
        }

        if (mode === 'create' || !itemId) {
            return `${resourceName}.urlCard.${mode}`
        }

        return `${resourceName}.urlCard.${mode}.${itemId}`
    },

    /**
     * Генерує кастомний ключ з валідацією
     * Використовується для специфічних випадків
     * 
     * @param parts - частини ключа
     * @returns ключ реєстру
     */
    custom: (...parts: (string | number)[]): string => {
        if (parts.length === 0) {
            throw new Error('[generateRegistryKey.custom] Потрібно передати хоча б одну частину ключа')
        }

        const filteredParts = parts.filter(part => 
            part !== undefined && part !== null && part !== ''
        )

        if (filteredParts.length === 0) {
            throw new Error('[generateRegistryKey.custom] Усі частини ключа порожні')
        }

        return filteredParts.join('.')
    },
}

/**
 * Валідує ключ реєстру
 * Перевіряє чи ключ відповідає конвенції іменування
 * 
 * @param key - ключ для валідації
 * @returns true якщо ключ валідний
 */
export const validateRegistryKey = (key: string): boolean => {
    if (!key || typeof key !== 'string') {
        return false
    }

    // Ключ повинен містити хоча б одну крапку (ResourceName.Purpose)
    if (!key.includes('.')) {
        console.warn(
            `[validateRegistryKey] Ключ "${key}" не містить крапки. ` +
            `Рекомендований формат: "ResourceName.Purpose"`
        )
        return false
    }

    // Ключ не повинен починатися або закінчуватися крапкою
    if (key.startsWith('.') || key.endsWith('.')) {
        console.warn(
            `[validateRegistryKey] Ключ "${key}" починається або закінчується крапкою`
        )
        return false
    }

    // Ключ не повинен містити подвійні крапки
    if (key.includes('..')) {
        console.warn(
            `[validateRegistryKey] Ключ "${key}" містить подвійні крапки`
        )
        return false
    }

    return true
}

/**
 * Парсить ключ реєстру на складові частини
 * 
 * @param key - ключ реєстру
 * @returns об'єкт з частинами ключа
 */
export const parseRegistryKey = (key: string): {
    resourceName: string
    type?: 'list' | 'card' | 'nested' | 'urlList' | 'urlCard'
    purpose?: string
    mode?: string
    itemId?: string
    parentResource?: string
    parentId?: string
} => {
    if (!validateRegistryKey(key)) {
        throw new Error(`[parseRegistryKey] Невалідний ключ: "${key}"`)
    }

    const parts = key.split('.')

    // Мінімум 2 частини: ResourceName.Purpose
    if (parts.length < 2) {
        throw new Error(`[parseRegistryKey] Ключ повинен містити хоча б 2 частини: "${key}"`)
    }

    const result: any = {
        resourceName: parts[0],
    }

    // Розпізнаємо тип по другій частині
    switch (parts[1]) {
        case 'card':
            result.type = 'card'
            result.mode = parts[2]
            result.itemId = parts[3]
            break

        case 'nested':
            result.type = 'nested'
            result.parentResource = parts[2]
            result.parentId = parts[3]
            break

        case 'urlList':
            result.type = 'urlList'
            break

        case 'urlCard':
            result.type = 'urlCard'
            result.mode = parts[2]
            result.itemId = parts[3]
            break

        default:
            result.type = 'list'
            result.purpose = parts[1]
            break
    }

    return result
}

export default generateRegistryKey
